import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowBack, CalendarToday, AccountBalance, Payment, Schedule, Person, Shield, Description, Notifications as NotificationsIcon, CheckCircle, History } from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../services/apiService";
import CommentsSection from "./dashboard/components/CommentsSection";
import PolicyTab from "./LoanDetails/PolicyTab";
import TransactionsTab from "./LoanDetails/TransactionsTab";
import ContractsTab from "./LoanDetails/ContractsTab";
import NotificationsTab from "./LoanDetails/NotificationsTab";
import ClientTab from "./LoanDetails/ClientTab";
import ResolutionsTab from "./LoanDetails/ResolutionsTab";
import SubmitResolutionModal from "../components/SubmitResolutionModal";

const LoanDetails = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("schedule");
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [lastResolution, setLastResolution] = useState(null);
  const [loadingResolution, setLoadingResolution] = useState(false);

  // Get the source page from location state
  const sourcePage = location.state?.from || "Loans";

  // Tab configuration
  const tabs = [
    { id: "client", label: "Client", icon: Person },
    { id: "schedule", label: "Schedule", icon: Schedule },
    { id: "policy", label: "Policy", icon: Shield },
    { id: "transactions", label: "Transactions", icon: Payment },
    // { id: "contracts", label: "Contracts", icon: Description },
    { id: "notifications", label: "Notifications", icon: NotificationsIcon },
    { id: "resolutions", label: "Resolutions", icon: History },
    { id: "comments", label: "Comments", icon: null },

  ];

  // Format currency helper
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number helper
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Format date helper
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return "N/A";
    }
  };

  // Resolution type label helper
  const getResolutionTypeLabel = (type) => {
    const labels = {
      client_picked_up: "Client Picked Up",
      client_no_answer: "Client Didn't Pick Up",
      promise_to_pay: "Promise to Pay",
      payment_collected: "Payment Collected",
      resolved: "Resolved",
      wrong_number: "Wrong Number",
      dispute: "Dispute",
      other: "Other"
    };
    return labels[type] || type;
  };

  // Resolution badge color helper
  const getResolutionBadgeColor = (type) => {
    const colors = {
      client_picked_up: "bg-green-100 text-green-800",
      client_no_answer: "bg-yellow-100 text-yellow-800",
      promise_to_pay: "bg-blue-100 text-blue-800",
      payment_collected: "bg-green-100 text-green-800",
      resolved: "bg-purple-100 text-purple-800",
      wrong_number: "bg-red-100 text-red-800",
      dispute: "bg-orange-100 text-orange-800",
      other: "bg-gray-100 text-gray-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  // Format date/time for resolution display
  const formatDateTime = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return "N/A";
    }
  };

  // Fetch loan details
  const fetchLoanDetails = async () => {
    if (!loanId) return;

    setLoading(true);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_LOANS}/get-loan`,
        { id: loanId }
      );
      const data = await response.json();
      if (data.loan) {
        setLoan(data.loan);
      }
    } catch (error) {
      console.error("Error fetching loan details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch last resolution
  const fetchLastResolution = async () => {
    if (!loanId) return;

    setLoadingResolution(true);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/loan-resolutions`,
        { loanId }
      );
      const data = await response.json();
      const resolutions = data.resolutions || [];
      // Get the most recent resolution
      if (resolutions.length > 0) {
        const lastRes = resolutions[0];

        // If it's a promise_to_pay, fetch the promise status
        if (lastRes.resolutionType === 'promise_to_pay' && lastRes.promiseDate && lastRes.promiseAmount) {
          try {
            const promiseResponse = await apiService.post(
              `${process.env.REACT_APP_API_HOST_COLLECTIONS}/get-promise-status`,
              {
                loanId,
                promiseDate: lastRes.promiseDate,
                promiseAmount: lastRes.promiseAmount
              }
            );
            const promiseData = await promiseResponse.json();
            if (promiseData.promise) {
              lastRes.promiseStatus = promiseData.promise.status;
              lastRes.amountPaid = promiseData.promise.amountPaid;
              lastRes.fulfillmentPercentage = promiseData.promise.fulfillmentPercentage;
            }
          } catch (promiseError) {
            console.error("Error fetching promise status:", promiseError);
          }
        }

        setLastResolution(lastRes);
      }
    } catch (error) {
      console.error("Error fetching last resolution:", error);
    } finally {
      setLoadingResolution(false);
    }
  };

  useEffect(() => {
    if (loanId) {
      // Clear previous loan's data immediately to prevent showing stale data
      setLoan(null);
      setLastResolution(null);

      // Fetch new loan's data
      fetchLoanDetails();
      fetchLastResolution();
    }
  }, [loanId]);

  // Auto-check loan status every 15 seconds for payment-based removals
  useEffect(() => {
    if (!loanId) return;

    const checkInterval = setInterval(async () => {
      try {
        const response = await apiService.post(
          `${process.env.REACT_APP_API_HOST_LOANS}/get-loan`,
          { id: loanId }
        );
        const data = await response.json();

        // If loan is no longer late, it was removed from collections
        if (data.loan && data.loan.status !== 'late') {
          toast.success('This loan is no longer in collections (payment received)');
          setTimeout(() => {
            navigate('/assigned-loans');
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking loan status:', error);
      }
    }, 15000); // 15 seconds

    return () => clearInterval(checkInterval);
  }, [loanId, navigate]);

  const handleBack = () => {
    if (location.state?.returnPath) {
      navigate(location.state.returnPath);
    } else {
      navigate(-1);
    }
  };

  const handleResolutionSubmit = ({ shouldNavigateToNext, nextLoanId }) => {
    setShowResolutionModal(false);

    if (shouldNavigateToNext && nextLoanId) {
      // Navigate to next loan (don't fetch current loan's resolution - useEffect will handle new loan)
      navigate(`/loan/${nextLoanId}`, {
        state: {
          from: sourcePage,
          returnPath: location.state?.returnPath
        }
      });
    } else if (shouldNavigateToNext && !nextLoanId) {
      // No more loans available, notify user and redirect back
      toast.info("You've contacted every loan in your pile. Nothing left to work on right now.");
      setTimeout(() => {
        handleBack();
      }, 2000); // Give user time to read the message
    } else {
      // Staying on current page - refresh the resolution to show the one just submitted
      fetchLastResolution();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading loan details...</p>
        </div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 text-lg">Loan details not found.</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Breadcrumb and Back Button */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 hover:text-blue-600 transition-colors"
            >
              <ArrowBack fontSize="small" />
              <span>{sourcePage}</span>
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">Loan #{loan.loanNumber}</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Loan #{loan.loanNumber}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {loan.client?.personal?.fullName || "N/A"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowResolutionModal(true)}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium flex items-center gap-2"
              >
                <CheckCircle fontSize="small" />
                Submit Resolution
              </button>
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors font-medium"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Split View: Sidebar + Main Content */}
      <div className="flex">
        {/* Sidebar - Collections Summary */}
        <div className="w-80 bg-white border-r min-h-screen p-6 space-y-6">
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AccountBalance className="text-blue-600" />
              Collections Summary
            </h2>

            {/* Key Metrics */}
            <div className="space-y-3">
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <p className="text-xs text-gray-600 mb-1">Total Arrears</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(loan.due?.totalAllDue || loan.due?.totalDue || 0)}
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-xs text-gray-600 mb-1">Late Days</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatNumber(loan.lateDays || 0)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs text-gray-600 mb-1">Outstanding Balance</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(loan.balance?.total || 0)}
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-xs text-gray-600 mb-1">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(loan.paid?.total || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Arrears Breakdown */}
          <div className="border-t pt-6">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Arrears Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Principal Due</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(loan.balance?.principal || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Interest Due</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(loan.balance?.interest || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Fee Due</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(loan.balance?.fee || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Penalty Due</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(loan.balance?.penalty || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="text-gray-600 font-semibold">Total Due</span>
                <span className="font-bold text-gray-900">
                  {formatCurrency(loan.balance?.total || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Arrears by Age */}
          {loan.arrears && (
            <div className="border-t pt-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Arrears by Age</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">1-7 Days</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(loan.arrears._1 || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">8-14 Days</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(loan.arrears._8 || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">15-30 Days</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(loan.arrears._15 || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">31-89 Days</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(loan.arrears._31 || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">90+ Days</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(loan.arrears._90 || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Assignment Info */}
          {loan.assignment?.assignedAgentName && (
            <div className="border-t pt-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Person fontSize="small" className="text-blue-600" />
                Assigned To
              </h3>
              <p className="text-sm font-semibold text-gray-900">
                {loan.assignment.assignedAgentName}
              </p>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6">
          {/* Loan Information - Fixed Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Payment className="text-blue-600" />
              Loan Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Principal</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatCurrency(loan.principal || 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Interest Rate</p>
                <p className="text-base font-semibold text-gray-900">
                  {loan.interestRate ? `${loan.interestRate}%` : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Instalment Amount</p>
                <p className="text-base font-semibold text-gray-900">
                  {(() => {
                    // Find next unpaid instalment
                    const nextUnpaid = loan.instalments?.find(inst => !inst.paid && inst.status !== 'paid');

                    // If there's an overdue instalment, show its total due amount
                    if (nextUnpaid && nextUnpaid.status === 'late') {
                      return formatCurrency(nextUnpaid.totalDue || nextUnpaid.instalment || loan.instalment || 0);
                    }

                    // Otherwise show regular instalment amount
                    return formatCurrency(loan.instalment || 0);
                  })()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">First Instalment Date</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatDate(loan.firstInstalmentDate)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Loan Product</p>
                <p className="text-base font-semibold text-gray-900">
                  {loan.loanProduct?.name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p className="text-base font-semibold text-gray-900">
                  {loan.status || "N/A"}
                </p>
              </div>
              {loan.collections?.nextDueDate && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Next Due Date</p>
                  <p className="text-base font-semibold text-gray-900">
                    {formatDate(loan.collections.nextDueDate)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Last Call Resolution */}
          {lastResolution && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <History className="text-orange-600" />
                Last Call Resolution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Resolution Type</p>
                  {lastResolution.resolutionType === 'promise_to_pay' ? (
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                      lastResolution.promiseStatus === 'fulfilled' ? 'bg-green-100 text-green-800' :
                      lastResolution.promiseStatus === 'broken' ? 'bg-red-100 text-red-800' :
                      lastResolution.promiseStatus === 'partially_fulfilled' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {lastResolution.promiseStatus === 'fulfilled' ? 'Promise Fulfilled' :
                       lastResolution.promiseStatus === 'broken' ? 'Promise Broken' :
                       lastResolution.promiseStatus === 'partially_fulfilled' ? 'Promise Partial' :
                       'Promise Pending'}
                    </span>
                  ) : (
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getResolutionBadgeColor(lastResolution.resolutionType)}`}>
                      {getResolutionTypeLabel(lastResolution.resolutionType)}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                  <p className="text-base font-semibold text-gray-900">
                    {formatDateTime(lastResolution.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Agent</p>
                  <p className="text-base font-semibold text-gray-900">
                    {lastResolution.agentName || "N/A"}
                  </p>
                </div>
                {lastResolution.resolutionType === 'promise_to_pay' && lastResolution.promiseDate && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Promise Details</p>
                    <p className="text-sm font-semibold text-blue-900">
                      {formatCurrency(lastResolution.promiseAmount)}
                    </p>
                    <p className="text-xs text-gray-600">
                      Due: {formatDate(lastResolution.promiseDate)}
                    </p>
                    {lastResolution.promiseStatus === 'partially_fulfilled' && lastResolution.fulfillmentPercentage > 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        {lastResolution.fulfillmentPercentage}% paid ({formatCurrency(lastResolution.amountPaid || 0)})
                      </p>
                    )}
                  </div>
                )}
                {lastResolution.resolutionType === 'payment_collected' && lastResolution.promiseAmount && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Amount Collected</p>
                    <p className="text-base font-semibold text-green-700">
                      {formatCurrency(lastResolution.promiseAmount)}
                    </p>
                  </div>
                )}
              </div>
              {lastResolution.notes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Notes</p>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">
                    {lastResolution.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          {!lastResolution && !loadingResolution && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                No call resolutions have been submitted for this loan yet.
              </p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-white rounded-t-lg border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 font-medium text-sm transition-colors relative ${
                    activeTab === tab.id
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {tab.icon && <tab.icon fontSize="small" />}
                    <span>{tab.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 shadow-sm">
            {/* Loan Schedule Tab */}
            {activeTab === "schedule" && (
              <div className="overflow-x-auto">
              {loan.instalments && loan.instalments.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Principal
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Interest
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fee
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Penalty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loan.instalments.map((instalment, index) => {
                      const dueDate = instalment.dueDate || instalment.date;
                      const isOverdue = dueDate && new Date(dueDate) < new Date();
                      const isPaid = instalment.paid || instalment.status === 'paid' || instalment.status === 'fulfilled';
                      const isPartial = instalment.paid && instalment.paid < (instalment.amount || instalment.total);

                      return (
                        <tr
                          key={index}
                          className={`${isOverdue && !isPaid
                              ? "bg-red-50"
                              : isPaid
                                ? "bg-green-50"
                                : isPartial
                                  ? "bg-yellow-50"
                                  : ""
                            } hover:bg-gray-50`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(dueDate)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                            {formatCurrency(instalment.instalment || instalment.amount || instalment.total || 0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                            {formatCurrency(instalment.principal || 0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                            {formatCurrency(instalment.interest || 0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                            {formatCurrency(instalment.fee || 0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                            {formatCurrency(instalment.penalty || 0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isPaid ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Paid
                              </span>
                            ) : isPartial ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Partial
                              </span>
                            ) : isOverdue ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                Overdue
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No schedule available for this loan.
                </div>
              )}
            </div>
            )}

            {/* Comments Tab */}
            {activeTab === "comments" && (
              <div className="p-6">
                <CommentsSection loanId={loanId} clientId={loan.clientId} />
              </div>
            )}

            {/* Policy Tab */}
            {activeTab === "policy" && (
              <PolicyTab loan={loan} />
            )}

            {/* Transactions Tab */}
            {activeTab === "transactions" && (
              <TransactionsTab loan={loan} />
            )}

            {/* Contracts Tab */}
            {activeTab === "contracts" && (
              <ContractsTab loanId={loanId} />
            )}

            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <NotificationsTab loanId={loanId} />
            )}

            {/* Resolutions Tab */}
            {activeTab === "resolutions" && (
              <ResolutionsTab loanId={loanId} />
            )}

            {/* Client Information Tab */}
            {activeTab === "client" && (
              <ClientTab loan={loan} />
            )}
          </div>
        </div>
      </div>

      {/* Submit Resolution Modal */}
      {showResolutionModal && (
        <SubmitResolutionModal
          loan={loan}
          onClose={() => setShowResolutionModal(false)}
          onSubmitSuccess={handleResolutionSubmit}
        />
      )}
    </div>
  );
};

export default LoanDetails;
